import type { AxiosResponse } from 'axios';
import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import type {
  AIChatFeedbackPayload,
  AIChatMessagePayload,
  GetAIChatSessionsParams,
  SaveAIChatQuestionPayload,
} from '@/components/AIChatDrawer/types';

export const GetAIChatSessions = (
  token?: string,
  params?: GetAIChatSessionsParams
): Promise<AxiosResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.search) {
    searchParams.set('search', params.search);
  }
  if (params?.page != null) {
    searchParams.set('page', String(params.page));
  }
  if (params?.page_size != null) {
    searchParams.set('page_size', String(params.page_size));
  }
  const query = searchParams.toString();
  const url = query
    ? `${APIPaths.aiInsightsChatbotSessions}?${query}`
    : APIPaths.aiInsightsChatbotSessions;

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('Error while fetching AI chat sessions:', error?.response);
        reject(error?.response);
      });
  });
};

export const StartAIChatSession = (token?: string): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.aiInsightsChatbotStart, {}, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('Error while starting AI chat session:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetAIChatSession = (
  sessionId: string,
  token?: string,
  signal?: AbortSignal
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.aiInsightsChatbotSession}${encodeURIComponent(sessionId)}/`,
        token,
        undefined,
        signal
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('Error while fetching AI chat session:', error?.response);
        reject(error?.response);
      });
  });
};

export const SendAIChatMessage = (
  payload: AIChatMessagePayload,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.aiInsightsChatbotChat, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('Error while sending AI chat message:', error?.response);
        reject(error?.response);
      });
  });
};

export const CreateAIChatFeedback = (
  payload: AIChatFeedbackPayload,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.aiInsightsChatbotFeedback, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('Error while creating AI chat feedback:', error?.response);
        reject(error?.response);
      });
  });
};

export const UpdateAIChatFeedback = (
  payload: AIChatFeedbackPayload,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut('Put', APIPaths.aiInsightsChatbotFeedback, payload, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log('Error while updating AI chat feedback:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetAIChatSavedQuestions = (
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.aiInsightsChatbotSavedQuestions, token)
      .then((res) => resolve(res))
      .catch((error) => {
        console.log(
          'Error while fetching AI chat saved questions:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const CreateAIChatSavedQuestion = (
  payload: SaveAIChatQuestionPayload,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.aiInsightsChatbotSavedQuestions,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.log(
          'Error while creating AI chat saved question:',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const DeleteAIChatSavedQuestion = (
  id: string,
  token?: string
): Promise<AxiosResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete(
        'Delete',
        `${APIPaths.aiInsightsChatbotSavedQuestions}${encodeURIComponent(id)}/`,
        undefined,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        console.log(
          'Error while deleting AI chat saved question:',
          error?.response
        );
        reject(error?.response);
      });
  });
};
